// Amplitude client for the iOS and Android app (./index.ts) and the web app
// (./index.web.js). It replaces Segment's SDK and analytics.js, which forwarded
// everything to Amplitude through Segment's Amplitude destination (the native
// source was repeated into the "Javascript" source).
//
// It is plain TypeScript over fetch and a key-value storage (AsyncStorage in
// the app, localStorage on the web), with no native code, so it can ship as an
// over-the-air update to builds that still contain the Segment SDK.
//
// It keeps what Segment produced in Amplitude, so existing charts carry on:
// - the same event names and properties, screens as "Viewed <name> Screen";
// - Segment's lifecycle events and their properties;
// - the same device_id: Segment's context.device.id (the iOS vendor id),
//   otherwise its anonymousId. Both, plus the user id and the last app
//   version, are carried over from Segment's storage on the first run, so an
//   existing install doesn't look new (on the web: analytics.js's anonymous
//   and user ids, through `previousIdentity`);
// - location: Amplitude looks up country and city from the upload's IP
//   ("$remote"), as it did from the IP Segment passed on.

export type KeyValueStorage = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
};

// Amplitude HTTP V2 device fields, sent with every event.
export type DeviceContext = {
  platform: string;
  os_name?: string | null;
  os_version?: string | null;
  device_model?: string | null;
  device_manufacturer?: string | null;
  device_brand?: string | null;
  app_version?: string | null;
  language?: string | null;
  // Web: Amplitude parses the browser and OS out of it.
  user_agent?: string | null;
};

export type AppInfo = { version: string | null; build: string | null };

type Properties = Record<string, unknown>;

export type AmplitudeEvent = {
  event_type: string;
  event_properties?: Properties;
  user_properties?: Properties;
  user_id?: string;
  device_id: string;
  session_id: number;
  time: number;
  insert_id: string;
  library: string;
  ip: string;
} & DeviceContext;

type State = {
  deviceId: string;
  // Segment kept the iOS vendor id across sign-outs and only replaced its
  // anonymous id, so reset() does the same.
  deviceIdIsVendorId: boolean;
  userId: string | null;
  sessionId: number | null;
  lastEventAt: number | null;
  // Version and build seen on the previous launch.
  appVersion: string | null;
  appBuild: string | null;
};

export type ClientOptions = {
  apiKey: string;
  storage: KeyValueStorage;
  context: () => DeviceContext;
  app: AppInfo;
  uuid: () => string;
  // The iOS identifierForVendor, which Segment used as the device id.
  vendorId?: () => Promise<string | null>;
  // Segment's write key, to carry the device and user over from its storage.
  segmentWriteKey?: string;
  // Another device and user to carry over on the first run (the web app's
  // analytics.js ids).
  previousIdentity?: () => { deviceId?: string | null; userId?: string | null } | null;
  library?: string;
  fetch?: typeof fetch;
  now?: () => number;
  serverUrl?: string;
  flushIntervalMs?: number;
  flushQueueSize?: number;
  maxQueueSize?: number;
  sessionTimeoutMs?: number;
  log?: (message: string, details?: unknown) => void;
};

export const STATE_KEY = "eventlive.amplitude.state";
export const QUEUE_KEY = "eventlive.amplitude.queue";
const SERVER_URL = "https://api2.amplitude.com/2/httpapi";
const LIBRARY = "eventlive-react-native/1";
const MAX_BATCH = 50;
const MIN_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 5 * 60_000;
const SAVE_DELAY_MS = 500;
const BACKGROUND_STATES = ["inactive", "background"];

// Never sent to Amplitude, even when a caller passes them along: profiles and
// event records carry stream keys and passwords, and profiles carry ad click
// ids (Amplitude isn't an ad platform; the Privacy Policy doesn't list them
// for it).
const SENSITIVE_KEY =
  /^(stream_key|event_password|download_pin|apikey|api_key|_?fbp|_?fbc|_?ga|gclid|msclkid|gbraid|wbraid|ttclid)$|token|password|secret/i;

// A copy of `props` without sensitive keys or undefined values. The caller's
// object is left alone (AuthContext stores the identify payload as the profile).
export function withoutSensitive(props?: Properties | null): Properties {
  const out: Properties = {};
  for (const [key, value] of Object.entries(props || {})) {
    if (value === undefined || SENSITIVE_KEY.test(key)) continue;
    out[key] = value;
  }
  return out;
}

function parse<T>(json: string | null): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function withoutEmpty<T extends Record<string, unknown>>(value: T): T {
  const out = {} as T;
  for (const key of Object.keys(value) as (keyof T)[]) {
    if (value[key] !== undefined && value[key] !== null) out[key] = value[key];
  }
  return out;
}

export class AmplitudeClient {
  private readonly opts: ClientOptions;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly flushIntervalMs: number;
  private readonly flushQueueSize: number;
  private readonly maxQueueSize: number;
  private readonly sessionTimeoutMs: number;
  private readonly ready: Promise<void>;

  private state: State;
  private context: DeviceContext = { platform: "" };
  private queue: AmplitudeEvent[] = [];
  private flushing = false;
  private batchSize = MAX_BATCH;
  private backoffMs = 0;
  private retryAt = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushDueAt = Infinity;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: ClientOptions) {
    this.opts = opts;
    this.fetchImpl = opts.fetch || ((input, init) => fetch(input, init));
    this.now = opts.now || Date.now;
    this.flushIntervalMs = opts.flushIntervalMs ?? 10_000;
    this.flushQueueSize = opts.flushQueueSize ?? 20;
    this.maxQueueSize = opts.maxQueueSize ?? 500;
    this.sessionTimeoutMs = opts.sessionTimeoutMs ?? 30 * 60_000;
    this.state = this.freshState("", false);
    this.ready = this.load().catch((error) => {
      this.log("load failed", error);
      if (!this.state.deviceId) this.state = this.freshState(opts.uuid(), false);
    });
  }

  track(eventType: string, properties?: Properties | null): Promise<void> {
    const at = this.now();
    const event_properties = withoutSensitive(properties);
    return this.run(() => this.push(at, { event_type: eventType, event_properties }));
  }

  screen(name: string): Promise<void> {
    return this.track(`Viewed ${name} Screen`, { name });
  }

  // userId null or undefined: update the current (possibly anonymous) user.
  identify(userId: string | null | undefined, traits?: Properties | null): Promise<void> {
    const at = this.now();
    const set = withoutSensitive(traits);
    return this.run(() => {
      if (userId) this.state.userId = String(userId);
      if (Object.keys(set).length) {
        this.push(at, { event_type: "$identify", user_properties: { $set: set } });
      } else {
        this.scheduleSave();
      }
    });
  }

  reset(): Promise<void> {
    return this.run(() => {
      this.state.userId = null;
      if (!this.state.deviceIdIsVendorId) this.state.deviceId = this.opts.uuid();
      this.scheduleSave();
    });
  }

  // Segment's launch events: Application Installed (no previous version),
  // Application Updated (a different previous version), then Application
  // Opened.
  trackLaunch(): Promise<void> {
    const at = this.now();
    const { version, build } = this.opts.app;
    return this.run(() => {
      const previous = this.state.appVersion;
      if (previous === null) {
        this.push(at, { event_type: "Application Installed", event_properties: { version, build } });
      } else if (previous !== version) {
        this.push(at, {
          event_type: "Application Updated",
          event_properties: {
            version,
            build,
            previous_version: previous,
            previous_build: this.state.appBuild,
          },
        });
      }
      this.push(at, {
        event_type: "Application Opened",
        event_properties: { from_background: false, version, build },
      });
      this.state.appVersion = version;
      this.state.appBuild = build;
      this.scheduleSave();
    });
  }

  // Segment's AppState handling, including its "unknown" starting state.
  trackAppStateChange(previous: string, next: string): Promise<void> {
    const { version, build } = this.opts.app;
    if (BACKGROUND_STATES.includes(previous) && next === "active") {
      return this.track("Application Opened", { from_background: true, version, build });
    }
    if ((previous === "active" || previous === "unknown") && BACKGROUND_STATES.includes(next)) {
      return this.track("Application Backgrounded").then(() => this.flush());
    }
    return Promise.resolve();
  }

  async flush(): Promise<void> {
    await this.ready;
    if (this.flushing || this.queue.length === 0) return;
    this.flushing = true;
    this.clearFlushTimer();
    const batch = this.queue.slice(0, this.batchSize);
    let retryInMs: number | null = null;
    try {
      const res = await this.fetchImpl(this.opts.serverUrl || SERVER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify({
          api_key: this.opts.apiKey,
          events: batch,
          options: { min_id_length: 1 },
        }),
      });
      if (res.ok) {
        this.drop(batch);
        this.backoffMs = 0;
        this.retryAt = 0;
        this.batchSize = MAX_BATCH;
      } else if (res.status === 413 && batch.length > 1) {
        this.batchSize = Math.ceil(batch.length / 2);
        retryInMs = 0;
      } else if (res.status === 400 || res.status === 413) {
        // Amplitude names the bad events by index; the rest can go again.
        // Without indexes the batch can never succeed, so it is dropped.
        const bad = await this.rejectedIndexes(res);
        const rejected = bad.length ? batch.filter((_, i) => bad.includes(i)) : batch;
        this.log(`dropped ${rejected.length} event(s), HTTP ${res.status}`);
        this.drop(rejected);
        retryInMs = 0;
      } else {
        retryInMs = this.backOff();
      }
    } catch (error) {
      this.log("send failed", error);
      retryInMs = this.backOff();
    } finally {
      this.flushing = false;
      this.scheduleSave();
    }
    if (this.queue.length) this.scheduleFlush(retryInMs ?? 0);
  }

  // For tests and diagnostics.
  async snapshot(): Promise<{ state: State; queue: AmplitudeEvent[] }> {
    await this.ready;
    return { state: { ...this.state }, queue: [...this.queue] };
  }

  private freshState(deviceId: string, deviceIdIsVendorId: boolean): State {
    return {
      deviceId,
      deviceIdIsVendorId,
      userId: null,
      sessionId: null,
      lastEventAt: null,
      appVersion: null,
      appBuild: null,
    };
  }

  private async load() {
    this.context = withoutEmpty(this.opts.context());
    const saved = parse<State>(await this.opts.storage.getItem(STATE_KEY));
    if (saved?.deviceId) {
      this.state = { ...this.freshState(saved.deviceId, false), ...saved };
    } else {
      this.state = (await this.fromSegment()) || this.fromPrevious() || (await this.newDevice());
    }
    const queued = parse<AmplitudeEvent[]>(await this.opts.storage.getItem(QUEUE_KEY));
    if (Array.isArray(queued)) this.queue = queued.slice(-this.maxQueueSize);
    await this.save();
    if (this.queue.length) this.scheduleFlush(0);
  }

  // Segment's sovran stores: "<writeKey>-userInfo" holds { userInfo:
  // { anonymousId, userId } } and "<writeKey>-context" holds { context:
  // { device: { id }, app: { version, build } } }.
  private async fromSegment(): Promise<State | null> {
    const key = this.opts.segmentWriteKey;
    if (!key) return null;
    const userInfo = parse<{ userInfo?: { anonymousId?: string; userId?: string | null } }>(
      await this.opts.storage.getItem(`${key}-userInfo`)
    )?.userInfo;
    const context = parse<{ context?: { device?: { id?: string }; app?: { version?: string; build?: string } } }>(
      await this.opts.storage.getItem(`${key}-context`)
    )?.context;
    const vendorId = context?.device?.id || null;
    const deviceId = vendorId || userInfo?.anonymousId;
    if (!deviceId) return null;
    return {
      ...this.freshState(deviceId, Boolean(vendorId)),
      userId: userInfo?.userId || null,
      appVersion: context?.app?.version ?? null,
      appBuild: context?.app?.build ?? null,
    };
  }

  private fromPrevious(): State | null {
    const previous = this.opts.previousIdentity?.();
    if (!previous?.deviceId) return null;
    return { ...this.freshState(previous.deviceId, false), userId: previous.userId || null };
  }

  private async newDevice(): Promise<State> {
    const vendorId = this.opts.vendorId ? await this.opts.vendorId().catch(() => null) : null;
    return this.freshState(vendorId || this.opts.uuid(), Boolean(vendorId));
  }

  private run(task: () => void | Promise<void>): Promise<void> {
    return this.ready.then(task).catch((error) => this.log("failed", error));
  }

  private push(at: number, event: Pick<AmplitudeEvent, "event_type"> & Partial<AmplitudeEvent>) {
    const { state } = this;
    if (state.sessionId === null || state.lastEventAt === null || at - state.lastEventAt > this.sessionTimeoutMs) {
      state.sessionId = at;
    }
    state.lastEventAt = Math.max(at, state.lastEventAt ?? 0);
    this.queue.push(
      withoutEmpty({
        ...this.context,
        ...event,
        user_id: state.userId || undefined,
        device_id: state.deviceId,
        session_id: state.sessionId,
        time: at,
        insert_id: this.opts.uuid(),
        library: this.opts.library || LIBRARY,
        ip: "$remote",
      })
    );
    if (this.queue.length > this.maxQueueSize) {
      const dropped = this.queue.splice(0, this.queue.length - this.maxQueueSize);
      this.log(`queue full, dropped ${dropped.length} oldest event(s)`);
    }
    this.scheduleFlush(this.queue.length >= this.flushQueueSize ? 0 : this.flushIntervalMs);
    this.scheduleSave();
  }

  private drop(events: AmplitudeEvent[]) {
    const gone = new Set(events);
    this.queue = this.queue.filter((event) => !gone.has(event));
  }

  private async rejectedIndexes(res: Response): Promise<number[]> {
    const body = parse<Record<string, unknown>>(await res.text().catch(() => null));
    const indexes = new Set<number>();
    for (const field of ["events_with_invalid_fields", "events_with_missing_fields", "events_with_invalid_id_lengths"]) {
      const byField = body?.[field];
      if (byField && typeof byField === "object") {
        for (const list of Object.values(byField as Record<string, unknown>)) {
          if (Array.isArray(list)) list.forEach((i) => typeof i === "number" && indexes.add(i));
        }
      }
    }
    for (const field of ["silenced_events"]) {
      const list = body?.[field];
      if (Array.isArray(list)) list.forEach((i) => typeof i === "number" && indexes.add(i));
    }
    return [...indexes];
  }

  private backOff(): number {
    this.backoffMs = Math.min(MAX_BACKOFF_MS, this.backoffMs ? this.backoffMs * 2 : MIN_BACKOFF_MS);
    this.retryAt = this.now() + this.backoffMs;
    return this.backoffMs;
  }

  private scheduleFlush(delayMs: number) {
    const dueAt = Math.max(this.now() + delayMs, this.retryAt);
    if (this.flushTimer && this.flushDueAt <= dueAt) return;
    this.clearFlushTimer();
    this.flushDueAt = dueAt;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushDueAt = Infinity;
      void this.flush();
    }, Math.max(0, dueAt - this.now()));
  }

  private clearFlushTimer() {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.flushDueAt = Infinity;
  }

  private scheduleSave() {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, SAVE_DELAY_MS);
  }

  private async save() {
    try {
      await this.opts.storage.setItem(STATE_KEY, JSON.stringify(this.state));
      await this.opts.storage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      this.log("save failed", error);
    }
  }

  private log(message: string, details?: unknown) {
    this.opts.log?.(`Amplitude: ${message}`, details);
  }
}
